import { useEffect, useState } from 'react';
import type { Exercise, TrainingSession } from '../lib/api';

// One input shape for the whole drawer: a sunken well that lifts to white and
// takes an ink edge on focus. `INPUT_NUM` is the same field for the numeric
// prescriptions, where the figures have to line up column to column.
const INPUT =
  'w-full rounded-control bg-bg-sunken border border-line px-3 py-2.5 text-sm text-fg placeholder:text-fg-faint transition-colors focus:outline-none focus:border-accent focus:bg-bg-raised';
const INPUT_NUM = `${INPUT} font-mono tabular-nums`;

type Props = {
  open: boolean;
  session: TrainingSession;
  onClose: () => void;
  onSave: (next: TrainingSession) => Promise<void>;
  onReset: () => Promise<void>;
};

export function EditSessionDrawer({ open, session, onClose, onSave, onReset }: Props) {
  const [draft, setDraft] = useState<TrainingSession>(session);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(session);
    setError(null);
  }, [session, open]);

  if (!open) return null;

  const updateExercise = (i: number, patch: Partial<Exercise>) => {
    const next = [...(draft.exercises ?? [])];
    next[i] = { ...next[i], ...patch };
    setDraft({ ...draft, exercises: next });
  };

  const removeExercise = (i: number) => {
    const next = [...(draft.exercises ?? [])];
    next.splice(i, 1);
    setDraft({ ...draft, exercises: next });
  };

  const addExercise = () => {
    const next: Exercise = { name: 'New exercise', sets: 3, reps: '10' };
    setDraft({ ...draft, exercises: [...(draft.exercises ?? []), next] });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(draft);
      onClose();
    } catch (e) {
      setError(String(e));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    setError(null);
    try {
      await onReset();
      onClose();
    } catch (e) {
      setError(String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-fg/25 backdrop-blur-md p-4" onClick={onClose}>
      {/* A sheet of paper lifted off the page, not a panel welded to the bottom
          edge — same object as every other card, just deeper in the stack. */}
      <div
        className="surface-raised surface-modal w-full sm:max-w-lg max-h-[85vh] overflow-y-auto p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-baseline justify-between sticky top-0 bg-bg-raised -m-5 mb-0 px-5 pt-5 pb-3 z-10">
          <h2 className="font-display text-2xl">Edit session</h2>
          <button onClick={onClose} className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint hover:text-fg transition-colors px-2">
            Close
          </button>
        </header>

        <div className="space-y-2">
          <Field label="Title">
            <input
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              className={INPUT}
            />
          </Field>
          <Field label="Notes">
            <textarea
              value={draft.notes ?? ''}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              rows={2}
              className={`${INPUT} resize-none`}
            />
          </Field>
        </div>

        <div className="space-y-3">
          <h3 className="label-eyebrow">Exercises</h3>
          {(draft.exercises ?? []).map((ex, i) => (
            <div key={i} className="rounded-panel border border-line p-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <input
                  value={ex.name}
                  onChange={(e) => updateExercise(i, { name: e.target.value })}
                  className={`${INPUT} flex-1 font-medium`}
                />
                <button
                  onClick={() => removeExercise(i)}
                  className="font-mono text-[10px] uppercase tracking-[0.16em] text-danger hover:opacity-80 transition-opacity px-2 shrink-0"
                >
                  Remove
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Sets">
                  <input
                    type="number"
                    value={ex.sets}
                    onChange={(e) => updateExercise(i, { sets: Math.max(0, Number(e.target.value)) })}
                    className={INPUT_NUM}
                  />
                </Field>
                <Field label="Reps">
                  <input
                    value={ex.reps}
                    onChange={(e) => updateExercise(i, { reps: e.target.value })}
                    className={INPUT}
                  />
                </Field>
                <Field label="Load">
                  <input
                    value={ex.load ?? ''}
                    onChange={(e) => updateExercise(i, { load: e.target.value })}
                    className={INPUT}
                  />
                </Field>
                <Field label="Rest (s)">
                  <input
                    type="number"
                    value={ex.rest_sec ?? 0}
                    onChange={(e) => updateExercise(i, { rest_sec: Math.max(0, Number(e.target.value)) })}
                    className={INPUT_NUM}
                  />
                </Field>
                <Field label="Hold timer (s)">
                  <input
                    type="number"
                    value={ex.timer_sec ?? 0}
                    onChange={(e) => updateExercise(i, { timer_sec: Math.max(0, Number(e.target.value)) })}
                    className={INPUT_NUM}
                  />
                </Field>
                <Field label="Work (s)">
                  <input
                    type="number"
                    value={ex.work_sec ?? 0}
                    onChange={(e) => updateExercise(i, { work_sec: Math.max(0, Number(e.target.value)) })}
                    className={INPUT_NUM}
                  />
                </Field>
                <Field label="Rest int. (s)">
                  <input
                    type="number"
                    value={ex.rest_int_sec ?? 0}
                    onChange={(e) => updateExercise(i, { rest_int_sec: Math.max(0, Number(e.target.value)) })}
                    className={INPUT_NUM}
                  />
                </Field>
                <Field label="Tempo note">
                  <input
                    value={ex.tempo_note ?? ''}
                    onChange={(e) => updateExercise(i, { tempo_note: e.target.value })}
                    className={INPUT}
                  />
                </Field>
              </div>
              <Field label="YouTube URL">
                <input
                  value={ex.video_url ?? ''}
                  onChange={(e) => updateExercise(i, { video_url: e.target.value })}
                  className={`${INPUT} font-mono`}
                />
              </Field>
            </div>
          ))}
          <button
            onClick={addExercise}
            className="w-full text-sm rounded-panel border border-dashed border-line-strong hover:border-accent text-fg-muted hover:text-fg py-3 transition-colors"
          >
            + Add exercise
          </button>
        </div>

        {error && <pre className="text-xs text-danger whitespace-pre-wrap break-all">{error}</pre>}

        <div className="flex gap-2 sticky bottom-0 bg-bg-raised -mx-5 -mb-5 px-5 pt-3 pb-5 z-10">
          <button
            onClick={reset}
            disabled={saving}
            className="btn-secondary flex-1 text-sm py-3"
          >
            Reset to default
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="btn-primary flex-1 text-sm py-3"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="label-eyebrow block mb-1">{label}</span>
      {children}
    </label>
  );
}
